
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Plus, BookOpen, Lock } from 'lucide-react';
import { MODEL_TYPES } from './constants';
import { ModelType, PredictionModel, Symbol as StrategySymbol, BacktestSettingsType } from './types';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ModelItem } from './model-selection/ModelItem'; // Use named import
import { useAccountType } from '@/hooks/useAccountType';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

export interface ModelSelectionProps {
  models: PredictionModel[];
  /** list of selected symbols for backtest */
  symbols: StrategySymbol[];
  /** backtest settings context */
  backtestSettings: BacktestSettingsType;
  onAddModel: (modelType: ModelType) => void;
  onRemoveModel: (index: number) => void;
  onUpdateModel: (index: number, changes: Partial<PredictionModel>) => void;
}

const ModelSelection: React.FC<ModelSelectionProps> = ({
  models,
  symbols,
  backtestSettings,
  onAddModel,
  onRemoveModel,
  onUpdateModel
}) => {
  const { hasModelAccess, getAvailableModels, accountType, permissions } = useAccountType();
  const availableModelIds = getAvailableModels();
  const [selectedType, setSelectedType] = useState<ModelType>('arima'); // Default to ARIMA for quant
  const [openAccordions, setOpenAccordions] = useState<number[]>([]);

  const toggleAccordion = (index: number) => {
    setOpenAccordions(prev => 
      prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
    );
  };

  const handleModelTypeChange = (index: number, type: string) => {
    onUpdateModel(index, { type: type as ModelType });
  };

  const handleParameterChange = (index: number, parameter: string, value: any) => {
    onUpdateModel(index, { [parameter]: value });
  };

  // Filter models based on account permissions (currently unused - could be used to hide locked models entirely)
  const filteredModelTypes = MODEL_TYPES.filter(model => hasModelAccess(model.id));

  return (
    <TooltipProvider>
      <div className="space-y-4">
        {/* Account Type Banner */}
        <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="capitalize">
              {accountType} Account
            </Badge>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Available Models: {availableModelIds.length}
            </span>
          </div>
          <div className="text-xs text-gray-500">
            {permissions?.aumCapacity ?? '—'} AUM Capacity
          </div>
        </div>

        {models.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-6 border border-dashed rounded-md bg-gray-50 dark:bg-gray-800">
            <BookOpen className="h-8 w-8 text-gray-400" />
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">No models added yet</p>
            <p className="text-xs text-gray-500 mt-1">Add a predictive model to power this strategy</p>
          </div>
        ) : (
        <ScrollArea className="h-[300px] pr-3">
          <div className="space-y-3">
            {models.map((model, index) => (
              <ModelItem
                key={index}
                model={model}
                index={index}
                isAccordionOpen={openAccordions.includes(index)}
                onRemoveModel={onRemoveModel}
                onModelTypeChange={handleModelTypeChange}
                onParameterChange={handleParameterChange}
                onToggleAccordion={toggleAccordion}
                canRemove={models.length > 1}
                symbols={symbols}
                backtestSettings={backtestSettings}
              />
            ))}
          </div>
        </ScrollArea>
      )}
      
      <div className="flex items-end gap-2 pt-2">
        <div className="flex-1 space-y-1">
          <label className="text-sm font-medium">Model Type</label>
          <Select
            value={selectedType}
            onValueChange={(value: ModelType) => setSelectedType(value)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select model type" />
            </SelectTrigger>
            <SelectContent>
              {MODEL_TYPES.map((model) => {
                const hasAccess = hasModelAccess(model.id);
                return (
                  <SelectItem 
                    key={model.id} 
                    value={model.id}
                    disabled={!hasAccess}
                  >
                    <div className="flex items-center justify-between w-full">
                      <span className={!hasAccess ? 'text-gray-400' : ''}>
                        {model.name}
                      </span>
                      {!hasAccess && (
                        <Lock className="h-3 w-3 text-gray-400 ml-2" aria-label="Upgrade to access this model" />
                      )}
                    </div>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              onClick={() => {
                if (hasModelAccess(selectedType)) {
                  onAddModel(selectedType);
                  // Automatically open the accordion for the newly added model
                  setOpenAccordions(prev => [...prev, models.length]);
                }
              }}
              variant="outline"
              size="sm"
              className="flex items-center gap-1"
              disabled={!hasModelAccess(selectedType)}
            >
              <Plus className="h-4 w-4" />
              Add Model
            </Button>
          </TooltipTrigger>
          {!hasModelAccess(selectedType) && (
            <TooltipContent>
              <p>Upgrade to access this model</p>
            </TooltipContent>
          )}
        </Tooltip>
      </div>
      </div>
    </TooltipProvider>
  );
};

export default ModelSelection; // Export as default
